import * as React from "react"

// Time a closing layer gets to finish its exit animation (300ms at most)
// before it is considered stalled.
const STALLED_EXIT_MS = 1000

const GUARDED_LAYERS = [
  "sheet-overlay",
  "sheet-content",
  "dialog-overlay",
  "dialog-content",
  "alert-dialog-overlay",
  "alert-dialog-content",
]
const GUARDED_SELECTOR = GUARDED_LAYERS.map(
  (slot) => `[data-slot="${slot}"]`,
).join(",")

const timers = new WeakMap<HTMLElement, number>()
let observer: MutationObserver | null = null

function watch(node: HTMLElement) {
  if (node.dataset.state !== "closed") {
    window.clearTimeout(timers.get(node))
    timers.delete(node)
    return
  }
  if (timers.has(node)) return
  timers.set(
    node,
    window.setTimeout(() => {
      timers.delete(node)
      if (!node.isConnected || node.dataset.state !== "closed") return
      node.dispatchEvent(
        new AnimationEvent("animationend", {
          animationName: getComputedStyle(node).animationName,
        }),
      )
    }, STALLED_EXIT_MS),
  )
}

// Radix keeps a closing overlay or content mounted until its exit
// `animationend`. When the browser drops that event, the closed layer never
// unmounts: it keeps `pointer-events: none` on the body, so nothing on the
// page can be clicked, and a reopen stacks the new overlay over it. Ending a
// stalled exit ourselves lets Radix unmount the layer. One observer on the
// body watches every layer: the overlay's ref does not reach its DOM node.
export function useStalledExitGuard() {
  React.useEffect(() => {
    if (observer) return
    observer = new MutationObserver((mutations) => {
      for (const { target } of mutations) {
        if (target instanceof HTMLElement && target.matches(GUARDED_SELECTOR)) {
          watch(target)
        }
      }
    })
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-state"],
      subtree: true,
    })
  }, [])
}
